/**
 * Resolve protected /media/ URLs to a short-lived signed API URL for viewers
 * (document viewers, etc.) that fetch resources without Authorization headers.
 */

import { getUploadEndpoint } from './upload';

/** Relative path prefix for content editor uploads stored under MEDIA_ROOT */
const PROTECTED_MEDIA_MARKERS = [
    '/uploads/content/documents/',
    '/uploads/content/media/',
    '/emg_uploads/content/documents/',
    '/emg_uploads/content/media/',
];

const MEDIA_PATH_MARKER = '/media/';

/**
 * Canonical storage form for uploaded media: ``/media/{relativeKey}`` (no host, no query).
 * Accepts absolute URLs such as ``https://ai-studio-uat.vmb.edu.vn/media/emg_uploads/...``.
 */
export function normalizeStorageMediaUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

    try {
        const parsed = trimmed.startsWith('http://') || trimmed.startsWith('https://')
            ? new URL(trimmed)
            : new URL(trimmed, 'http://local');
        const pos = parsed.pathname.indexOf(MEDIA_PATH_MARKER);
        if (pos >= 0) {
            return parsed.pathname.slice(pos).split('?')[0].split('#')[0];
        }
    } catch {
        /* fall through */
    }

    const pos = trimmed.indexOf(MEDIA_PATH_MARKER);
    if (pos >= 0) {
        return trimmed.slice(pos).split('?')[0].split('#')[0];
    }
    return trimmed;
}

const SIGNED_URL_MAX_ATTEMPTS = 6;
const SIGNED_URL_RETRY_BASE_MS = 100;

/**
 * In-memory cache for resolved signed URLs.
 * Prevents duplicate POST /api/media/signed-url calls for the same media path.
 * TTL = 10 minutes (signed URLs typically last 15-60 min).
 */
const SIGNED_URL_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedSignedUrl {
    url: string;
    pdfPreviewUrl?: string;
    publicUrl?: string;
    expiresAt: number;
}

const signedUrlCache = new Map<string, CachedSignedUrl>();
/** De-duplicate in-flight requests for the same media path */
const signedUrlInflight = new Map<string, Promise<CachedSignedUrl | null>>();

/**
 * Negative cache: remember media paths that failed to resolve (non-401 errors
 * like 404) so we don't keep hammering the API on every component re-render.
 * Short TTL (60s) allows eventual recovery if the endpoint becomes available.
 */
const SIGNED_URL_NEGATIVE_CACHE_TTL_MS = 60 * 1000;
const signedUrlNegativeCache = new Map<string, number>();

function getCachedSignedUrl(mediaPath: string): CachedSignedUrl | undefined {
    const entry = signedUrlCache.get(mediaPath);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
        signedUrlCache.delete(mediaPath);
        return undefined;
    }
    return entry;
}

function setCachedSignedUrl(mediaPath: string, entry: Omit<CachedSignedUrl, 'expiresAt'>): void {
    signedUrlCache.set(mediaPath, {
        ...entry,
        expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS,
    });
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function getApiBaseUrlFromUploadEndpoint(): string {
    const ep = getUploadEndpoint().trim();
    try {
        if (ep.startsWith('http://') || ep.startsWith('https://')) {
            const u = new URL(ep);
            let path = u.pathname.replace(/\/?$/, '');
            path = path.replace(/\/api\/upload$/i, '').replace(/\/upload$/i, '');
            if (!path || path === '/') {
                return u.origin;
            }
            return `${u.origin}${path}`;
        }
        if (typeof window === 'undefined') {
            return '';
        }
        const u = new URL(ep.startsWith('/') ? ep : `/${ep}`, window.location.origin);
        let path = u.pathname.replace(/\/?$/, '');
        path = path.replace(/\/api\/upload$/i, '').replace(/\/upload$/i, '');
        if (!path || path === '/') {
            return u.origin;
        }
        return `${u.origin}${path}`;
    } catch {
        return '';
    }
}

export function shouldResolveMediaUrlForViewer(src: string): boolean {
    const s = normalizeStorageMediaUrl(src).trim().toLowerCase();
    if (!s) return false;
    if (s.startsWith('data:') || s.startsWith('blob:')) return false;
    // Path form (/serve/...) or query form (/serve?k=...) — already a short-lived link.
    if (s.includes('/api/media/serve')) return false;
    return PROTECTED_MEDIA_MARKERS.some((m) => s.includes(m));
}

type TiptapMediaNode = {
    type?: string;
    attrs?: Record<string, unknown>;
    content?: TiptapMediaNode[];
};

/**
 * Resolve protected image/audio ``src`` attrs in a Tiptap doc to short-lived
 * ``/api/media/serve/?k=...`` URLs for viewer rendering.
 */
export async function resolveTiptapDocMediaUrls<T extends TiptapMediaNode>(doc: T): Promise<T> {
    const cloned = structuredClone(doc) as T;
    const tasks: Promise<void>[] = [];

    const walk = (node: TiptapMediaNode): void => {
        if (node.type === 'image' || node.type === 'audio') {
            const rawSrc = node.attrs?.src;
            if (typeof rawSrc === 'string' && shouldResolveMediaUrlForViewer(rawSrc)) {
                const storageSrc = normalizeStorageMediaUrl(rawSrc);
                tasks.push(
                    resolveMediaUrlForViewer(storageSrc).then((resolved) => {
                        if (node.attrs) {
                            node.attrs = { ...node.attrs, src: resolved || storageSrc };
                        }
                    }),
                );
            }
        }
        node.content?.forEach(walk);
    };

    cloned.content?.forEach(walk);
    await Promise.all(tasks);
    return cloned;
}

/**
 * EAA-1857: resolve protected media referenced from H5P ``params`` for the player.
 *
 * H5P locates every asset through ``H5P.getPath(path, contentId)``, which returns the
 * value untouched only when it already has a scheme; anything else is treated as a file
 * inside the content folder and gets ``.../content/<id>/`` prepended. Our uploads are
 * stored as root-relative ``/media/...`` keys behind a signed-URL endpoint, so the
 * player asked for ``/content/<id>//media/...`` and every image 404'd — a Moodle
 * drag-and-drop import rendered its drop zones over a broken background.
 *
 * ACH content never hit this because its players run each ``src`` through
 * ``resolveMediaUrlForViewer`` first; the H5P player bypasses that path entirely. This
 * walker applies the same resolution to the params tree before handing it over.
 *
 * H5P has no single media node type — an asset is any object carrying a ``path``
 * (``settings.background``, ``elements[].type.params.file``, ``audio[]``, …), so the
 * walk is key-driven rather than type-driven and works for every library.
 */
export async function resolveH5PParamsMediaUrls<T>(params: T): Promise<T> {
    return (await resolveH5PParamsMediaUrlsWithOriginals(params)).params;
}

/** Maps a resolved (signed) URL back to the storage key it was resolved from. */
export type H5PMediaOriginals = Record<string, string>;

export interface H5PMediaResolution<T> {
    params: T;
    /** Pass to ``restoreH5PParamsMediaUrls`` before persisting edited params. */
    originals: H5PMediaOriginals;
}

/**
 * As ``resolveH5PParamsMediaUrls``, but also reports how to undo the swap.
 *
 * The editor needs this and the player does not: ``H5PEditor.getContent()`` reads params
 * back out of the H5P editor and saves them, so a signed URL substituted for display
 * would be written into the stored content and break for good once it expired.
 * ``normalizeStorageMediaUrl`` cannot recover the key on its own — a signed link looks
 * like ``/api/media/serve/?k=…``, whose first ``/media/`` segment belongs to the endpoint
 * path, not the asset — so the original is recorded here instead of re-derived.
 */
export async function resolveH5PParamsMediaUrlsWithOriginals<T>(
    params: T,
): Promise<H5PMediaResolution<T>> {
    const originals: H5PMediaOriginals = {};
    if (!params || typeof params !== 'object') return { params, originals };

    const cloned = structuredClone(params) as T;
    const tasks: Promise<void>[] = [];

    const visit = (node: unknown): void => {
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        if (!node || typeof node !== 'object') return;

        const record = node as Record<string, unknown>;
        const rawPath = record.path;
        if (typeof rawPath === 'string' && shouldResolveMediaUrlForViewer(rawPath)) {
            const storagePath = normalizeStorageMediaUrl(rawPath);
            tasks.push(
                resolveMediaUrlForViewer(storagePath).then((resolved) => {
                    const next = resolved || storagePath;
                    record.path = next;
                    if (next !== rawPath) originals[next] = rawPath;
                }),
            );
        }
        Object.values(record).forEach(visit);
    };

    visit(cloned);
    await Promise.all(tasks);
    return { params: cloned, originals };
}

/**
 * Undo ``resolveH5PParamsMediaUrlsWithOriginals`` so edited params persist storage keys
 * rather than the short-lived signed URLs that were only ever for display.
 *
 * Paths the editor author added or changed are left alone — they are not in the map.
 */
export function restoreH5PParamsMediaUrls<T>(params: T, originals: H5PMediaOriginals): T {
    if (!params || typeof params !== 'object' || Object.keys(originals).length === 0) {
        return params;
    }

    const cloned = structuredClone(params) as T;

    const visit = (node: unknown): void => {
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        if (!node || typeof node !== 'object') return;

        const record = node as Record<string, unknown>;
        const current = record.path;
        if (typeof current === 'string' && originals[current]) {
            record.path = originals[current];
        }
        Object.values(record).forEach(visit);
    };

    visit(cloned);
    return cloned;
}

type WinGlobals = Window & {
    __UPLOAD_GET_HEADERS__?: () => Record<string, string>;
    __SHARED_API_AUTH_HEADERS__?: () => Record<string, string>;
};

function hasApiCredentialHeaders(h: Record<string, string>): boolean {
    const auth = h.Authorization?.trim();
    if (auth) return true;
    return Boolean(h['X-Public-Key']?.trim());
}

/**
 * Headers for POST /api/media/signed-url: upload globals, then window hook from
 * configureApi(), then a fresh read from @ai-content-hub/shared-api when present
 * (avoids stale closures / race before configureApi runs).
 */
async function getMergedAuthHeadersForMediaSignedUrl(): Promise<Record<string, string>> {
    const w = typeof window !== 'undefined' ? (window as WinGlobals) : undefined;

    const fromUpload = { ...(w?.__UPLOAD_GET_HEADERS__?.() ?? {}) };
    const fromSharedHook = { ...(w?.__SHARED_API_AUTH_HEADERS__?.() ?? {}) };

    let fromModule: Record<string, string> = {};
    try {
        const mod = await import('@ai-content-hub/shared-api');
        if (mod.isApiConfigured?.()) {
            fromModule = { ...(mod.getSharedApiAuthHeaders?.() ?? {}) };
        }
    } catch {
        // Optional peer: bundle without shared-api (unlikely for apps using this helper).
    }

    return {
        ...fromUpload,
        ...fromSharedHook,
        ...fromModule,
        'Content-Type': 'application/json',
    };
}

export interface ResolvedMediaUrls {
    url: string;
    pdfPreviewUrl?: string;
    /** S3 presigned URL accessible by external services (Office Online). */
    publicUrl?: string;
    /**
     * Whether ``url`` is safe to fetch as-is.
     *
     * ``true`` when the source needed no signing, or when signing succeeded.
     * ``false`` when the source DID need signing but signing failed and ``url`` is the
     * bare, unsigned ``/media/...`` storage path — a path that is not served as the
     * document by anything except the backend's own media route.
     *
     * EAA-1501: this flag exists because the fallback used to be indistinguishable from
     * success. A viewer would fetch the unsigned path, an SPA/CDN host would answer it
     * with `200 text/html` (its index page), `response.ok` was true, and pdf.js then
     * reported "Could not open this PDF file." — pointing the reader at the *file* for
     * what is actually a *URL resolution* failure. Callers must check this before
     * blaming the document.
     */
    resolved: boolean;
}

/** Shape parsed out of POST /api/media/signed-url; `resolved` is decided by the caller. */
type SignedUrlPayload = Omit<ResolvedMediaUrls, 'resolved'>;

function parseSignedUrlResponse(_res: Response, body: unknown): string {
    const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    const data =
        obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : {};
    return (
        (typeof data.url === 'string' && data.url) ||
        (typeof obj.url === 'string' && obj.url) ||
        ''
    );
}

function parseSignedUrlResponseFull(_res: Response, body: unknown): SignedUrlPayload {
    const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    const data =
        obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : {};
    const url =
        (typeof data.url === 'string' && data.url) ||
        (typeof obj.url === 'string' && obj.url) ||
        '';
    const pdfPreviewUrl =
        (typeof data.pdf_preview_url === 'string' && data.pdf_preview_url) ||
        (typeof obj.pdf_preview_url === 'string' && obj.pdf_preview_url) ||
        undefined;
    const publicUrl =
        (typeof data.public_url === 'string' && data.public_url) ||
        (typeof obj.public_url === 'string' && obj.public_url) ||
        undefined;
    return { url, pdfPreviewUrl, publicUrl };
}

/**
 * Internal: fetch signed URL from API and cache the result.
 * De-duplicates in-flight requests for the same media path.
 */
async function fetchAndCacheSignedUrl(trimmed: string): Promise<CachedSignedUrl | null> {
    // Check in-memory cache first
    const cached = getCachedSignedUrl(trimmed);
    if (cached) return cached;

    // Check negative cache — skip API call if this path recently failed
    const negExpiry = signedUrlNegativeCache.get(trimmed);
    if (negExpiry != null) {
        if (Date.now() < negExpiry) {
            return null;
        }
        signedUrlNegativeCache.delete(trimmed);
    }

    // De-duplicate in-flight requests
    const inflight = signedUrlInflight.get(trimmed);
    if (inflight) return inflight;

    const base = getApiBaseUrlFromUploadEndpoint();
    if (!base) return null;

    const promise = (async (): Promise<CachedSignedUrl | null> => {
        const endpoint = `${base.replace(/\/+$/, '')}/api/media/signed-url`;
        const bodyJson = JSON.stringify({ media_url: trimmed });

        for (let attempt = 0; attempt < SIGNED_URL_MAX_ATTEMPTS; attempt++) {
            const headers = await getMergedAuthHeadersForMediaSignedUrl();
            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: bodyJson,
            });

            const body: unknown = await res.json().catch(() => null);

            if (res.ok) {
                const parsed = parseSignedUrlResponseFull(res, body);
                if (parsed.url) {
                    const entry = {
                        url: parsed.url,
                        pdfPreviewUrl: parsed.pdfPreviewUrl,
                        publicUrl: parsed.publicUrl,
                    };
                    setCachedSignedUrl(trimmed, entry);
                    return { ...entry, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS };
                }
                return null;
            }

            if (res.status !== 401) {
                // Cache the failure so we don't retry on every re-render
                signedUrlNegativeCache.set(trimmed, Date.now() + SIGNED_URL_NEGATIVE_CACHE_TTL_MS);
                return null;
            }
            if (hasApiCredentialHeaders(headers)) return null;

            if (attempt < SIGNED_URL_MAX_ATTEMPTS - 1) {
                await delay(SIGNED_URL_RETRY_BASE_MS * (attempt + 1));
            }
        }

        return null;
    })();

    signedUrlInflight.set(trimmed, promise);
    try {
        return await promise;
    } finally {
        signedUrlInflight.delete(trimmed);
    }
}

/**
 * If *src* points at a protected local media path, POST to ``/api/media/signed-url``
 * with auth headers and return the signed ``data.url``. Otherwise return *src*.
 *
 * Results are cached in-memory for 10 minutes to prevent repeated API calls
 * when components re-render.
 *
 * Requires the same credentials as other Content Hub APIs (Bearer / Token / Session /
 * public key). Retries a few times on 401 only while no credential headers are present,
 * so a late configureApi() or token hydrate can succeed.
 */
export async function resolveMediaUrlForViewer(src: string): Promise<string> {
    const trimmed = normalizeStorageMediaUrl(src.trim());
    if (!trimmed || !shouldResolveMediaUrlForViewer(trimmed)) {
        return trimmed;
    }

    const result = await fetchAndCacheSignedUrl(trimmed);
    return result?.url || trimmed;
}

/**
 * Like ``resolveMediaUrlForViewer`` but returns both the main URL and an
 * optional ``pdfPreviewUrl`` (set by the backend when a LibreOffice PDF
 * preview is available for PPTX/PPT files).
 *
 * Results are cached in-memory for 10 minutes.
 */
export async function resolveMediaUrlsForViewer(src: string): Promise<ResolvedMediaUrls> {
    const trimmed = normalizeStorageMediaUrl(src.trim());
    if (!trimmed || !shouldResolveMediaUrlForViewer(trimmed)) {
        // Nothing to sign (already a signed/serve link, a data: URI, an external URL…),
        // so the URL is as good as it will get — that counts as resolved.
        return { url: trimmed, resolved: true };
    }

    const result = await fetchAndCacheSignedUrl(trimmed);
    if (result?.url) {
        return {
            url: result.url,
            pdfPreviewUrl: result.pdfPreviewUrl,
            publicUrl: result.publicUrl,
            resolved: true,
        };
    }
    // EAA-1501: keep returning the storage path so an already-public deployment still
    // works, but say out loud that it is unsigned. Silence here is what made a broken
    // signed-url call look like a broken PDF.
    console.warn(
        '[mediaUrl] POST /api/media/signed-url did not return a URL; falling back to the '
            + 'unsigned storage path. Anything fetching it may receive an error page '
            + 'instead of the document.',
        { mediaPath: trimmed },
    );
    return { url: trimmed, resolved: false };
}
